import { useCallback, useRef } from 'react';
import { Play } from 'lucide-react';
import type { StringSet } from '@/music/tuning';
import type { Fingering } from '@/music/voicing';
import { useHoldRoll } from '@/audio/useHoldRoll';
import { useHoldStrum } from '@/audio/useHoldStrum';

interface HoldGesture {
  pressing: boolean;
  begin: () => void;
  end: () => void;
  cancel: () => void;
}

interface HoldButtonProps {
  gesture: HoldGesture;
  fillRef: React.RefObject<HTMLSpanElement>;
  className?: string;
  style?: React.CSSProperties;
  disabled?: boolean;
  /** The visible word beside the icon; omit for an icon-only button. */
  label?: string;
  ariaLabel: string;
  testId?: string;
}

/** The press-and-hold shell both instruments' play buttons share: a fill bar
 * sweeps left→right while held; pointer, touch and keyboard all drive it. */
function HoldButton({
  gesture,
  fillRef,
  className,
  style,
  disabled,
  label,
  ariaLabel,
  testId,
}: HoldButtonProps) {
  const { pressing, begin, end, cancel } = gesture;
  return (
    <button
      type="button"
      className={`strum-btn ${className ?? ''}`}
      style={style}
      disabled={disabled}
      data-pressing={pressing}
      data-testid={testId}
      onPointerDown={(e) => {
        e.preventDefault();
        try {
          e.currentTarget.setPointerCapture(e.pointerId);
        } catch {
          /* ignore */
        }
        begin();
      }}
      onPointerUp={end}
      onPointerCancel={cancel}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && !e.repeat) {
          e.preventDefault();
          begin();
        }
      }}
      onKeyUp={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          end();
        }
      }}
      aria-label={ariaLabel}
    >
      <span ref={fillRef} className="strum-fill" aria-hidden="true" />
      <span className="strum-label">
        <Play size={14} fill="currentColor" aria-hidden="true" />
        {label}
      </span>
    </button>
  );
}

function useFill() {
  const fillRef = useRef<HTMLSpanElement>(null);
  const setFill = useCallback((fraction: number) => {
    if (fillRef.current) fillRef.current.style.width = `${Math.min(1, fraction) * 100}%`;
  }, []);
  return { fillRef, setFill };
}

interface PlayHoldButtonProps {
  fingering: Fingering;
  stringSet: StringSet;
  className?: string;
  style?: React.CSSProperties;
  disabled?: boolean;
  label?: string;
  testId?: string;
}

/**
 * Press-and-hold play control with a fill bar. A quick tap plays a block chord;
 * holding fills the button left→right (up to 1 s) and, on release, strums the
 * chord as a single ascending roll spanning the held time. Mouse/touch/keyboard.
 */
export function PlayHoldButton({
  fingering,
  stringSet,
  className,
  style,
  disabled,
  label = 'Play',
  testId,
}: PlayHoldButtonProps) {
  const { fillRef, setFill } = useFill();
  const gesture = useHoldStrum(fingering, stringSet, { onProgress: setFill });
  return (
    <HoldButton
      gesture={gesture}
      fillRef={fillRef}
      className={className}
      style={style}
      disabled={disabled}
      label={label}
      testId={testId}
      ariaLabel="Play voicing — hold to set the strum length"
    />
  );
}

interface PianoPlayHoldButtonProps {
  midis: readonly number[];
  className?: string;
  style?: React.CSSProperties;
  disabled?: boolean;
  label?: string;
  testId?: string;
}

/** The same control for a keyboard stack: a tap plays it at the pianist's
 * spread, a hold rolls it out over the time held. */
export function PianoPlayHoldButton({
  midis,
  className,
  style,
  disabled,
  label = 'Play',
  testId,
}: PianoPlayHoldButtonProps) {
  const { fillRef, setFill } = useFill();
  const gesture = useHoldRoll(midis, { onProgress: setFill });
  return (
    <HoldButton
      gesture={gesture}
      fillRef={fillRef}
      className={className}
      style={style}
      disabled={disabled}
      label={label}
      testId={testId}
      ariaLabel="Play voicing — hold to set the roll length"
    />
  );
}
